import React, { useState, useRef, useEffect } from "react";
import useTheme from "../useTheme";
import { getExerciseById } from "../data/exercises";
import * as api from "../api";
import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";

const POSE_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";

function landmarksFromPoseResult(result) {
  if (!result?.landmarks?.[0]) return null;
  return result.landmarks[0].map((lm) => ({
    x: lm.x, y: lm.y, z: lm.z ?? 0, visibility: lm.visibility ?? 1,
  }));
}

const POSE_CONNECTIONS = [
  [11,12],[11,13],[13,15],[12,14],[14,16],
  [23,24],[11,23],[12,24],
  [23,25],[25,27],[24,26],[26,28],
  [27,29],[29,31],[28,30],[30,32],
];

function drawPoseOnCanvas(canvas, video, landmarks, postureOk) {
  if (!canvas || !video || !landmarks || landmarks.length < 33) return;
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return;
  if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, w, h);
  const color = postureOk === true ? "#22c55e" : postureOk === false ? "#ef4444" : "rgba(150,150,150,0.8)";
  const lineWidth = Math.max(2, Math.min(4, w / 160));
  const radius = Math.max(3, Math.min(6, w / 120));
  ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = lineWidth; ctx.lineCap = "round";
  for (const [i, j] of POSE_CONNECTIONS) {
    if (i >= landmarks.length || j >= landmarks.length) continue;
    const a = landmarks[i]; const b = landmarks[j];
    if (!a || !b || (a.visibility !== undefined && a.visibility < 0.3) || (b.visibility !== undefined && b.visibility < 0.3)) continue;
    ctx.beginPath(); ctx.moveTo(a.x * w, a.y * h); ctx.lineTo(b.x * w, b.y * h); ctx.stroke();
  }
  for (let i = 0; i < landmarks.length; i++) {
    if (i <= 10) continue;
    const lm = landmarks[i];
    if (!lm || (lm.visibility !== undefined && lm.visibility < 0.3)) continue;
    ctx.beginPath(); ctx.arc(lm.x * w, lm.y * h, radius, 0, 2 * Math.PI); ctx.fill();
  }
}

const LANDMARK_ALPHA = 0.35;
const CORRECT_STABLE_MS = 450;

function emaLandmarks(prev, next) {
  if (!prev || prev.length !== next.length) return next;
  return next.map((lm, i) => ({
    x: prev[i].x * (1 - LANDMARK_ALPHA) + lm.x * LANDMARK_ALPHA,
    y: prev[i].y * (1 - LANDMARK_ALPHA) + lm.y * LANDMARK_ALPHA,
    z: prev[i].z * (1 - LANDMARK_ALPHA) + lm.z * LANDMARK_ALPHA,
    visibility: lm.visibility,
  }));
}

const difficultyStyle = {
  Beginner:     { bg: "rgba(34,197,94,0.15)",  border: "rgba(34,197,94,0.4)",  text: "#4ade80" },
  Intermediate: { bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.4)", text: "#fbbf24" },
  Advanced:     { bg: "rgba(239,68,68,0.15)",  border: "rgba(239,68,68,0.4)",  text: "#f87171" },
};

export default function ExercisePage({ exerciseId, onNavigate }) {
  const exercise = getExerciseById(exerciseId);
  const [mode, setMode] = useState(null);
  const [stream, setStream] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadExerciseId, setUploadExerciseId] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [liveFeedback, setLiveFeedback] = useState(null);
  const [liveError, setLiveError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseRef = useRef(null);
  const rafRef = useRef(null);
  const lastSendRef = useRef(0);
  const landmarksRef = useRef(null);
  const postureOkRef = useRef(undefined);
  const sessionIdRef = useRef(null);
  const repAccuraciesRef = useRef([]);
  const maxCounterRef = useRef(0);
  const startCounterRef = useRef(-1);
  const lastSpokenRef = useRef("");
  const lastSpokenTimeRef = useRef(0);
  const [isMuted, setIsMuted] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const [showWarmup, setShowWarmup] = useState(false);
  const [warmupSeconds, setWarmupSeconds] = useState(120);
  const warmupTimerRef = useRef(null);
  const pendingLiveStartRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const isMutedRef = useRef(false);

  const lastSpeakTimeRef = useRef(0);
  const synthKeepAliveRef = useRef(null);

  function speak(text, priority = false) {
    if (isMutedRef.current) return;
    const now = Date.now();
    const GAP = priority ? 2000 : 7000;
    if (now - lastSpeakTimeRef.current < GAP) return;
    if (text === lastSpokenRef.current && now - lastSpokenTimeRef.current < 12000) return;
    lastSpokenRef.current = text;
    lastSpokenTimeRef.current = now;
    lastSpeakTimeRef.current = now;
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.pitch = 1.0;
    u.volume = 1.0;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      const preferred =
        voices.find(v => v.lang === "en-US" && v.name.toLowerCase().includes("google")) ||
        voices.find(v => v.lang.startsWith("en") && !v.name.toLowerCase().includes("compact")) ||
        voices[0];
      if (preferred) u.voice = preferred;
    }
    if (synthKeepAliveRef.current) clearInterval(synthKeepAliveRef.current);
    synthKeepAliveRef.current = setInterval(() => {
      if (!window.speechSynthesis.speaking) {
        clearInterval(synthKeepAliveRef.current);
        synthKeepAliveRef.current = null;
        return;
      }
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }, 10000);
    u.onend = () => {
      if (synthKeepAliveRef.current) {
        clearInterval(synthKeepAliveRef.current);
        synthKeepAliveRef.current = null;
      }
    };
    window.speechSynthesis.speak(u);
  }

  function announce(text) {
    if (isMutedRef.current) return;
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.pitch = 1.0;
    u.volume = 1.0;
    window.speechSynthesis.speak(u);
  }

  const smoothLandmarksRef = useRef(null);
  const stableCorrectRef = useRef(null);
  const pendingCorrectRef = useRef(null);
  const pendingSinceRef = useRef(0);
  const plankIntervalRef = useRef(null);
  const plankSecondsRef = useRef(0);
  const totalPlankSecondsRef = useRef(0);
  const [plankSeconds, setPlankSeconds] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState(null);
  const [uploadVideoUrl, setUploadVideoUrl] = useState(null);

  const displayName = useState(() =>
    localStorage.getItem("pc_demo_username") || localStorage.getItem("pc_demo_email") || ""
  )[0];
  const userId = localStorage.getItem("pc_demo_user_id");

  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stream]);

  useEffect(() => {
    if (mode !== "live" || !videoRef.current) return;
    let s = null;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user" } })
      .then((mediaStream) => {
        s = mediaStream;
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch((err) => {
        console.error(err);
        setLiveError("Could not access camera. Please allow camera permission.");
        setAnalyzing(false);
      });
    return () => { if (s) s.getTracks().forEach((t) => t.stop()); };
  }, [mode]);

  useEffect(() => {
    if (mode !== "live" || !stream) return;
    let cancelled = false;
    async function loadPose() {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
        );
        poseRef.current = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: "GPU" },
          runningMode: "VIDEO", numPoses: 1,
        });
        setAnalyzing(false);
      } catch (e) {
        console.error("Pose model load error:", e);
        setLiveError("Could not load pose model. Check your connection.");
      }
    }
    async function runPoseLoop() {
      await loadPose();
      function loop(ts) {
        if (cancelled) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && video.readyState >= 2 && poseRef.current) {
          try {
            const result = poseRef.current.detectForVideo(video, ts);
            const lms = landmarksFromPoseResult(result);
            landmarksRef.current = lms;
            smoothLandmarksRef.current = emaLandmarks(smoothLandmarksRef.current, lms);
            const stableNow = Date.now();
            const rawOk = postureOkRef.current;
            if (rawOk === stableCorrectRef.current) {
              pendingCorrectRef.current = null;
            } else if (pendingCorrectRef.current !== rawOk) {
              pendingCorrectRef.current = rawOk;
              pendingSinceRef.current = stableNow;
            } else {
              const isHoldExercise = exerciseId === "tree-pose" || exerciseId === "plank";
              const requiredMs = isHoldExercise && rawOk === false ? 0 : CORRECT_STABLE_MS;
              if (stableNow - pendingSinceRef.current >= requiredMs) {
                stableCorrectRef.current = rawOk;
                pendingCorrectRef.current = null;
              }
            }
            drawPoseOnCanvas(canvas, video, smoothLandmarksRef.current || lms, stableCorrectRef.current);
            const now = Date.now();
            if (lms && now - lastSendRef.current > 100) {
              lastSendRef.current = now;
              api.streamAnalysis(exerciseId, lms).then((data) => {
                if (!cancelled) {
                  setLiveFeedback(data);
                  postureOkRef.current = data.posture_ok;
                  const msg = data.message || (data.posture_ok ? "Good form" : "Adjust your form");
                  speak(msg);
                  if (exerciseId === "plank" || exerciseId === "tree-pose") {
                    const shouldCount = data.posture_ok && stableCorrectRef.current === true;
                    if (shouldCount) {
                      if (!plankIntervalRef.current) {
                        plankIntervalRef.current = setInterval(() => {
                          if (!plankIntervalRef.current) return;
                          plankSecondsRef.current += 1;
                          totalPlankSecondsRef.current += 1;
                          setPlankSeconds(plankSecondsRef.current);
                          const s = plankSecondsRef.current;
                          announce(s === 1 ? "1 second" : s + " seconds");
                        }, 1000);
                      }
                    } else {
                      if (plankIntervalRef.current) {
                        clearInterval(plankIntervalRef.current);
                        plankIntervalRef.current = null;
                        window.speechSynthesis.cancel();
                      }
                    }
                  }
                  if (data.accuracy != null && data.accuracy > 0) repAccuraciesRef.current.push(data.accuracy);
                  if (data.counter !== undefined && data.counter !== null) {
                    if (startCounterRef.current === -1) startCounterRef.current = data.counter;
                    const relativeCount = data.counter - startCounterRef.current;
                    if (relativeCount > maxCounterRef.current) {
                      maxCounterRef.current = relativeCount;
                      if (exerciseId !== "plank" && exerciseId !== "tree-pose") {
                        announce("Rep " + relativeCount);
                      }
                    }
                  }
                }
              }).catch(() => {});
            }
          } catch (e) { console.error(e); }
        }
        rafRef.current = requestAnimationFrame(loop);
      }
      rafRef.current = requestAnimationFrame(loop);
    }
    runPoseLoop();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [mode, stream, exerciseId]);

  const [menuOpen, setMenuOpen] = React.useState(false);

  const isGuest = localStorage.getItem("pc_demo_user_id") === "0";

  function handleSignOut() {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    localStorage.removeItem("pc_demo_email");
    localStorage.removeItem("pc_demo_username");
    localStorage.removeItem("pc_demo_user_id");
    onNavigate("signin");
  }

  function handleBack() {
    // Auto-save recording if active
    stopRecording();
    const isTimer = exerciseId === "plank" || exerciseId === "tree-pose";
    const reps = isTimer ? totalPlankSecondsRef.current : (maxCounterRef.current || (liveFeedback ? liveFeedback.counter || 0 : 0));
    const acc = repAccuraciesRef.current.length > 0 ? Math.round(repAccuraciesRef.current.reduce((a,b) => a+b, 0) / repAccuraciesRef.current.length) : (liveFeedback && liveFeedback.accuracy ? Math.round(liveFeedback.accuracy) : 0);
    if (sessionIdRef.current) {
      if (!isGuest) api.endSession(sessionIdRef.current, reps, acc).catch(() => {});
      sessionIdRef.current = null;
    }
    if (mode === "live" && (reps > 0 || acc > 0)) {
      setSessionSummary({ reps, acc, isTimer, exerciseName: exercise ? exercise.name : exerciseId });
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setStream(null);
      return;
    }
    if (stream) stream.getTracks().forEach((t) => t.stop());
    totalPlankSecondsRef.current = 0; plankSecondsRef.current = 0;
    maxCounterRef.current = 0; repAccuraciesRef.current = [];
    setMode(null); setStream(null); setUploadFile(null);
    setUploadError(""); setLiveError(""); setLiveFeedback(null);
    setAnalyzing(false); setUploadSuccess(false); setUploadResult(null);
    onNavigate("dashboard");
  }


  async function handleDismissSummary() {
    // Auto-save review if stars selected
    const userId = localStorage.getItem("pc_demo_user_id");
    if (userId && !isGuest && reviewStars > 0 && reviewComment.trim() !== "") {
      await api.submitReview(userId, exerciseId, reviewStars, reviewComment, sessionIdRef.current).catch(() => {});
    }
    totalPlankSecondsRef.current = 0; plankSecondsRef.current = 0;
    maxCounterRef.current = 0; repAccuraciesRef.current = [];
    setMode(null); setStream(null); setUploadFile(null);
    setUploadError(""); setLiveError(""); setLiveFeedback(null);
    setAnalyzing(false); setUploadSuccess(false); setUploadResult(null);
    setSessionSummary(null);
    setReviewStars(0);
    setReviewComment("");
    setReviewSubmitted(false);
    onNavigate("dashboard");
  }

  function startLiveCamera() {
    // Show warm-up popup first
    setWarmupSeconds(120);
    setShowWarmup(true);
    pendingLiveStartRef.current = true;
    warmupTimerRef.current = setInterval(() => {
      setWarmupSeconds(prev => {
        if (prev <= 1) {
          clearInterval(warmupTimerRef.current);
          warmupTimerRef.current = null;
          setShowWarmup(false);
          startLiveCamera();
          return 120;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function startRecording() {
    if (!stream) return;
    recordedChunksRef.current = [];
    const options = { mimeType: "video/webm;codecs=vp8" };
    let recorder;
    try { recorder = new MediaRecorder(stream, options); }
    catch(e) { recorder = new MediaRecorder(stream); }
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exerciseId}_session_${new Date().toISOString().slice(0,19).replace(/:/g,"-")}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      recordedChunksRef.current = [];
    };
    recorder.start(100);
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
  }

  function stopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setIsRecording(false);
  }

  function handleLiveStart() {
    maxCounterRef.current = 0;
    startCounterRef.current = -1;
    repAccuraciesRef.current = [];
    totalPlankSecondsRef.current = 0;
    plankSecondsRef.current = 0;
    // Reset backend counter before starting live session
    api.streamAnalysis(exerciseId, [], true).catch(() => {});
    if (userId && !isGuest) {
      api.startSession(userId, api.toBackendExerciseType(exerciseId), "live")
        .then(data => { sessionIdRef.current = data.session_id; })
        .catch(() => {});
    }
    // Show warm-up popup first
    setWarmupSeconds(120);
    setShowWarmup(true);
    warmupTimerRef.current = setInterval(() => {
      setWarmupSeconds(prev => {
        if (prev <= 1) {
          clearInterval(warmupTimerRef.current);
          warmupTimerRef.current = null;
          setShowWarmup(false);
          setMode("live"); setUploadError(""); setLiveError(""); setLiveFeedback(null); setAnalyzing(true);
          return 120;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function handleResetCounter() {
    api.streamAnalysis(exerciseId, [], true).then((data) => setLiveFeedback(data)).catch(() => {});
  }

  function handleUploadChange(e) {
    const file = e.target.files?.[0];
    setUploadFile(file || null); setUploadExerciseId(exerciseId); setUploadError(""); setUploadSuccess(false); setUploadResult(null);
    if (file) setUploadVideoUrl(URL.createObjectURL(file));
    else setUploadVideoUrl(null);
  }

  async function handleUploadSubmit() {
    if (!uploadFile) { setUploadError("Please select a video file."); return; }
    if (!uploadExerciseId) { setUploadError("Please select which exercise is shown in the video."); return; }
    if (uploadExerciseId !== exerciseId) { setUploadError(`Use the correct exercise. This page is for ${exercise.name} only.`); return; }
    setUploadError(""); setUploadSuccess(false); setUploadResult(null); setAnalyzing(true);
    setUploadProgress(0);

    try {
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
      );
      // Use CPU delegate for mobile compatibility
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: isMobile ? "CPU" : "GPU" },
        runningMode: "VIDEO", numPoses: 1,
      });

      const videoEl = document.createElement("video");
      videoEl.src = URL.createObjectURL(uploadFile);
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.setAttribute("playsinline", "");
      videoEl.setAttribute("webkit-playsinline", "");
      await new Promise((res) => { videoEl.onloadedmetadata = res; });
      const duration = videoEl.duration;

      const accuracies = [];
      let maxReps = 0;
      let goodFrames = 0;
      let lastProcessedTime = -1;

      // Reset backend counter before each upload analysis
      await api.streamAnalysis(exerciseId, [], true).catch(() => {});

      await new Promise((resolve, reject) => {
        videoEl.ontimeupdate = async () => {
          try {
            const currentTime = videoEl.currentTime;
            // Process every 250ms to avoid overwhelming backend
            if (currentTime - lastProcessedTime < 0.25) return;
            lastProcessedTime = currentTime;

            const ts = Math.round(currentTime * 1000);
            const result = poseLandmarker.detectForVideo(videoEl, ts);
            const lms = landmarksFromPoseResult(result);
            if (lms) {
              const data = await api.streamAnalysis(exerciseId, lms).catch(() => null);
              if (data) {
                if (data.accuracy) accuracies.push(data.accuracy);
                if (data.counter && data.counter > maxReps) maxReps = data.counter;
                if (data.posture_ok) goodFrames += 1;
              }
            }
            setUploadProgress(Math.round((currentTime / duration) * 100));
            // Sync preview video
            const prev = document.getElementById("upload-preview-video");
            if (prev) prev.currentTime = currentTime;
          } catch(e) { reject(e); }
        };
        videoEl.onended = () => resolve();
        videoEl.onerror = reject;
        // Sync preview video
        const previewVid = document.getElementById("upload-preview-video");
        if (previewVid) { previewVid.currentTime = 0; previewVid.play().catch(()=>{}); }
        videoEl.play().catch(reject);
      });

      const avgAccuracy = accuracies.length > 0
        ? Math.round(accuracies.reduce((a,b) => a+b, 0) / accuracies.length) : 0;

      const holdSeconds = Math.round(goodFrames * 0.25);
      const tipsByExercise = {
        "tree-pose": {
          low:  ["Focus on a fixed point ahead to improve balance.", "Keep your standing leg fully straight.", "Press your foot firmly against your inner thigh, not the knee."],
          mid:  ["Try raising your arms overhead for a deeper stretch.", "Engage your core to hold longer.", "Try to extend hold time beyond 30 seconds."],
          high: ["Excellent balance! Try closing your eyes for an advanced challenge.", "Try holding for 60+ seconds for mastery.", "Focus on keeping hips perfectly level."],
        },
        "plank": {
          low:  ["Keep your hips level — don't let them sag or rise.", "Engage your core and glutes throughout.", "Look down at the floor to keep neck neutral."],
          mid:  ["Breathe steadily — don't hold your breath.", "Try to extend your hold time gradually.", "Keep shoulders directly over wrists."],
          high: ["Great plank! Try adding shoulder taps for more challenge.", "Aim for 60+ second holds consistently.", "Focus on full body tension from head to toe."],
        },
        "bicep-curl": {
          low:  ["Keep your elbows pinned to your sides — don't swing.", "Use a controlled motion, especially on the way down.", "Avoid using momentum — slow down the movement."],
          mid:  ["Squeeze at the top of each rep for maximum contraction.", "Try slowing the eccentric (lowering) phase to 3 seconds.", "Keep your wrists straight throughout."],
          high: ["Great form! Try increasing weight for progressive overload.", "Add a pause at the top for extra intensity.", "Try alternating arms for better isolation."],
        },
        "squat": {
          low:  ["Keep your knees tracking over your toes — don't let them cave in.", "Push your hips back first before bending knees.", "Keep your chest up and back straight."],
          mid:  ["Try to reach parallel depth or below for full range.", "Drive through your heels as you stand up.", "Keep weight evenly distributed across both feet."],
          high: ["Excellent squat form! Try adding weight for progression.", "Focus on a controlled 3-second descent.", "Try pause squats at the bottom for extra strength."],
        },
        "pushup": {
          low:  ["Keep your body in a straight line — no sagging hips.", "Lower your chest all the way to the floor.", "Keep elbows at 45 degrees, not flared wide."],
          mid:  ["Focus on full range of motion — chest to floor.", "Squeeze your glutes and core throughout.", "Try slowing down the descent to 3 seconds."],
          high: ["Great push-up form! Try diamond push-ups for variation.", "Add a pause at the bottom for extra challenge.", "Try archer push-ups for unilateral strength."],
        },
        "lunges": {
          low:  ["Keep your front knee above your ankle, not past your toes.", "Stand tall — don't lean forward.", "Lower your back knee straight down toward the floor."],
          mid:  ["Keep your torso upright throughout the movement.", "Take a longer stride for better range of motion.", "Focus on pushing through the front heel to stand up."],
          high: ["Great lunges! Try adding weights for progression.", "Try walking lunges for more dynamic challenge.", "Focus on keeping hips square throughout."],
        },
      };
      const exTips = tipsByExercise[exerciseId] || {};
      const suggestions = avgAccuracy < 60 ? exTips.low || [] : avgAccuracy < 85 ? exTips.mid || [] : exTips.high || [];
      const feedback = avgAccuracy >= 90 ? "Excellent form! Keep it up!"
        : avgAccuracy >= 75 ? "Good form! Small improvements will get you to perfect."
        : avgAccuracy >= 60 ? "Decent effort! Focus on the tips below to improve."
        : "Keep practicing! Use the suggestions below to improve your form.";

      const result = {
        total_reps: (exerciseId === "plank" || exerciseId === "tree-pose") ? holdSeconds : maxReps,
        avg_accuracy: avgAccuracy,
        feedback: feedback,
        suggestions: suggestions,
      };
      setUploadResult(result);
      setUploadSuccess(true);
      setUploadProgress(100);

      if (userId && !isGuest) {
        const sid = await api.startSession(userId, api.toBackendExerciseType(exerciseId), "upload").then(d => d.session_id).catch(() => null);
        if (sid) await api.endSession(sid, maxReps, avgAccuracy).catch(() => {});
      }

      poseLandmarker.close();
      URL.revokeObjectURL(videoEl.src);
    } catch (err) {
      setUploadError("Analysis failed: " + (err.message || "Unknown error"));
    } finally {
      setAnalyzing(false);
    }
  }

  if (!exercise) {
    return (
      <div className="dashboard-page">
        <p>Exercise not found.</p>
        <button type="button" className="btn primary" onClick={() => onNavigate("dashboard")}>Back to exercises</button>
      </div>
    );
  }

  const youtubeUrl = exercise.youtubeUrl || `https://www.youtube.com/results?search_query=${encodeURIComponent(`${exercise.name} exercise tutorial`)}`;
  const diff = difficultyStyle[exercise.difficulty] || difficultyStyle["Beginner"];

  if (sessionSummary) {
    const { reps, acc, isTimer, exerciseName } = sessionSummary;
    const feedback = acc >= 90 ? "Excellent form! You crushed it!" : acc >= 75 ? "Great job! Keep pushing!" : acc >= 60 ? "Good effort! Focus on form next time." : "Keep practicing — you're improving!";
    return (
      <div style={{ minHeight:"100vh", width:"100%", background: theme === "light" ? "linear-gradient(180deg, #dbeafe, #bfdbfe)" : "radial-gradient(1200px 600px at 10% 20%, rgba(124,58,237,0.1), transparent), linear-gradient(180deg,#0f172a,#0b3140)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Inter, ui-sans-serif, system-ui, sans-serif" }}>
        <div style={{ background: theme === "light" ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.05)", border: theme === "light" ? "1px solid rgba(30,64,175,0.3)" : "1px solid rgba(255,255,255,0.1)", borderRadius:24, padding:"24px 28px", maxWidth:480, width:"90%", textAlign:"center", boxShadow: theme === "light" ? "0 8px 32px rgba(0,0,0,0.12)" : "none" }}>
          <div style={{ fontSize:64, marginBottom:8 }}>🏆</div>
          <h1 style={{ margin:"0 0 8px", fontSize:28, fontWeight:800, color: theme === "light" ? "#0f172a" : "white" }}>Session Complete!</h1>
          <p style={{ margin:"0 0 12px", fontSize:16, color: theme === "light" ? "#475569" : "rgba(255,255,255,0.5)" }}>{exerciseName}</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
            <div style={{ background: theme === "light" ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.06)", border:"1px solid #6366f180", borderRadius:16, padding:"24px 16px" }}>
              <div style={{ fontSize:36, fontWeight:800, color:"#6366f1" }}>{isTimer ? `${reps}s` : reps}</div>
              <div style={{ fontSize:13, color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.4)", marginTop:4 }}>{isTimer ? "Hold Time" : "Total Reps"}</div>
            </div>
            <div style={{ background: theme === "light" ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.06)", border:"1px solid #06b6d480", borderRadius:16, padding:"24px 16px" }}>
              <div style={{ fontSize:36, fontWeight:800, color:"#06b6d4" }}>{acc}%</div>
              <div style={{ fontSize:13, color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.4)", marginTop:4 }}>Avg Accuracy</div>
            </div>
          </div>
          <div style={{ background: theme === "light" ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.12)", border:"1px solid rgba(99,102,241,0.2)", borderRadius:12, padding:"16px 20px", marginBottom:8, fontSize:15, color: theme === "light" ? "#4338ca" : "#a5b4fc" }}>{feedback}</div>
          {!reviewSubmitted ? (
            <div style={{ marginBottom:8, textAlign:"left" }}>
              <div style={{ fontSize:15, fontWeight:600, color: theme === "light" ? "#0f172a" : "white", marginBottom:6 }}>Rate your session</div>
              <div style={{ display:"flex", gap:8, marginBottom:6, justifyContent:"center" }}>
                {[1,2,3,4,5].map(s => (
                  <span key={s} onClick={() => setReviewStars(s)} style={{ fontSize:26, cursor:"pointer", filter: s <= reviewStars ? "none" : "grayscale(1)", transition:"all 0.15s" }}>⭐</span>
                ))}
              </div>
              <textarea
                value={reviewComment}
                onChange={e => !isGuest && setReviewComment(e.target.value)}
                placeholder={isGuest ? "Sign in to leave a comment..." : "Add a comment (optional)..."}
                rows={3}
                disabled={isGuest}
                style={{ width:"100%", borderRadius:10, border: theme === "light" ? "1px solid rgba(30,64,175,0.3)" : "1px solid rgba(255,255,255,0.15)", background: isGuest ? (theme === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.02)") : (theme === "light" ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.05)"), color: theme === "light" ? "#0f172a" : "white", padding:"10px 14px", fontSize:14, resize:"none", boxSizing:"border-box", outline:"none", marginBottom:10, cursor: isGuest ? "not-allowed" : "text", opacity: isGuest ? 0.5 : 1 }}
              />

            </div>
          ) : (
            <div style={{ marginBottom:24, padding:"14px", borderRadius:12, background:"rgba(6,182,212,0.1)", border:"1px solid rgba(6,182,212,0.3)", color:"#06b6d4", fontWeight:600, fontSize:14 }}>
              Thanks for your review!
            </div>
          )}
          <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
            <button onClick={handleDismissSummary} style={{ padding:"14px 28px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#7c3aed,#06b6d4)", color:"white", fontSize:15, fontWeight:700, cursor:"pointer" }}>Back to Exercises</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", width: "100%",
      background: theme === "light" ? "linear-gradient(180deg, #dbeafe, #bfdbfe)" : "radial-gradient(1200px 600px at 10% 20%, rgba(124,58,237,0.1), transparent), radial-gradient(800px 400px at 90% 80%, rgba(6,182,212,0.07), transparent), linear-gradient(180deg,#0f172a,#0b3140)",
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
      color: "#e6f7f9", display: "flex", flexDirection: "column",
    }}>

      {/* WARMUP MODAL */}
      {showWarmup && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)",
          zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "20px", boxSizing: "border-box",
        }}>
          <div style={{
            background: theme === "light" ? "white" : "#1e293b",
            borderRadius: "24px", padding: "28px 32px", maxWidth: "520px", width: "100%",
            boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
            border: theme === "light" ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.1)",
          }}>
            <h2 style={{ margin: "0 0 6px", fontSize: "22px", fontWeight: "800", color: theme === "light" ? "#0f172a" : "white", textAlign: "center" }}>
              Warm Up First!
            </h2>
            <p style={{ margin: "0 0 20px", fontSize: "14px", color: theme === "light" ? "#475569" : "rgba(255,255,255,0.6)", lineHeight: 1.6, textAlign: "center" }}>
              2 minutes of warm-up prevents injuries and improves performance.
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "24px", marginBottom: "24px" }}>
              <img src="/warmup.png" alt="warmup" style={{ width: "180px", height: "180px", objectFit: "contain" }} />
              <div style={{ position: "relative", width: "140px", height: "140px", flexShrink: 0 }}>
                <svg width="140" height="140" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="70" cy="70" r="60" fill="none" stroke={theme === "light" ? "#e2e8f0" : "rgba(255,255,255,0.1)"} strokeWidth="8" />
                  <circle cx="70" cy="70" r="60" fill="none" stroke="#7c3aed" strokeWidth="8"
                    strokeDasharray={String(2 * Math.PI * 60)}
                    strokeDashoffset={String(2 * Math.PI * 60 * (warmupSeconds / 120))}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 1s linear" }}
                  />
                </svg>
                <div style={{
                  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                  fontSize: "26px", fontWeight: "700", color: theme === "light" ? "#0f172a" : "white", textAlign: "center",
                }}>
                  {Math.floor(warmupSeconds / 60)}:{(warmupSeconds % 60).toString().padStart(2,"0")}
                  <div style={{ fontSize: "11px", fontWeight: "500", color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.5)", marginTop: "2px" }}>remaining</div>
                </div>
              </div>
            </div>
            <button onClick={() => {
              if (warmupTimerRef.current) { clearInterval(warmupTimerRef.current); warmupTimerRef.current = null; }
              setShowWarmup(false); setWarmupSeconds(120);
              setMode("live"); setUploadError(""); setLiveError(""); setLiveFeedback(null); setAnalyzing(true);
            }} style={{
              width: "100%", padding: "14px", borderRadius: "12px", border: "none", cursor: "pointer",
              background: "linear-gradient(135deg, #7c3aed, #06b6d4)", color: "white",
              fontSize: "16px", fontWeight: "700",
            }}>Skip it</button>
          </div>
        </div>
      )}

      {/* TOPBAR */}
      <header style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 32px", background: theme === "light" ? "rgba(255,255,255,0.9)" : "rgba(15,23,42,0.85)", backdropFilter:"blur(12px)", borderBottom: theme === "light" ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <div style={{ width:"40px", height:"40px", borderRadius:"10px", background:"linear-gradient(135deg,#7c3aed,#06b6d4)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:"700", fontSize:"14px", color:"white", boxShadow:"0 4px 14px rgba(124,58,237,0.3)" }}>PC</div>
          <span style={{ fontWeight:"600", fontSize:"16px", color: theme === "light" ? "#0f172a" : "#e6f7f9" }}>Pose Corrector AI</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          {/* Desktop buttons */}
          <button type="button" onClick={toggleTheme} className="su-theme-btn" style={{ fontSize:"13px", padding:"7px 14px", display: window.innerWidth < 640 ? "none" : "block" }}>{theme === "light" ? "🌙 Dark Mode" : "☀️ Light Mode"}</button>
          <button type="button" onClick={() => !isGuest && handleSignOut()} className="su-theme-btn" style={{ fontSize:"13px", padding:"7px 14px", display: window.innerWidth < 640 ? "none" : "block", opacity: isGuest ? 0.4 : 1, cursor: isGuest ? "not-allowed" : "pointer", filter: isGuest ? "grayscale(1)" : "none" }}>Sign out</button>
          {/* Mobile hamburger */}
          <button onClick={() => setMenuOpen(true)} style={{ display: window.innerWidth < 640 ? "flex" : "none", flexDirection:"column", justifyContent:"center", alignItems:"center", gap:"5px", background:"none", border:"none", cursor:"pointer", padding:"4px" }}>
            <span style={{ display:"block", width:"22px", height:"2px", background: theme === "light" ? "#0f172a" : "white", borderRadius:"2px" }} />
            <span style={{ display:"block", width:"22px", height:"2px", background: theme === "light" ? "#0f172a" : "white", borderRadius:"2px" }} />
            <span style={{ display:"block", width:"22px", height:"2px", background: theme === "light" ? "#0f172a" : "white", borderRadius:"2px" }} />
          </button>
        </div>
      </header>
      {/* Mobile slide-in menu */}
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1002 }}>
          <div onClick={e => e.stopPropagation()} style={{ position:"absolute", top:0, right:0, width:"220px", height:"100%", background: theme === "light" ? "white" : "#0f172a", boxShadow:"-4px 0 20px rgba(0,0,0,0.2)", padding:"24px 20px", display:"flex", flexDirection:"column", gap:"16px" }}>
            <button onClick={() => setMenuOpen(false)} style={{ alignSelf:"flex-end", background:"none", border:"none", fontSize:"22px", cursor:"pointer", color: theme === "light" ? "#0f172a" : "white" }}>✕</button>
            <button onClick={() => { toggleTheme(); setMenuOpen(false); }} className="su-theme-btn" style={{ width:"100%", textAlign:"center" }}>{theme === "light" ? "🌙 Dark Mode" : "☀️ Light Mode"}</button>
            <button onClick={() => { if(!isGuest){ handleSignOut(); setMenuOpen(false); } }} className="su-theme-btn" style={{ width:"100%", textAlign:"center", opacity: isGuest ? 0.4 : 1, cursor: isGuest ? "not-allowed" : "pointer", filter: isGuest ? "grayscale(1)" : "none" }}>Sign Out</button>
          </div>
        </div>
      )}

      <main style={{ flex:1, maxWidth:"1400px", width:"100%", margin:"0 auto", padding:"32px 48px 64px", boxSizing:"border-box" }}>

        {/* BACK */}
        <button type="button" onClick={() => {
          if (uploadSuccess && uploadResult) {
            const isTimer = exerciseId === "plank" || exerciseId === "tree-pose";
            setSessionSummary({ reps: uploadResult.total_reps, acc: uploadResult.avg_accuracy, isTimer, exerciseName: exercise ? exercise.name : exerciseId });
          } else {
            handleBack();
          }
        }} style={{ background:"none", border:"none", color: theme === "light" ? "#475569" : "rgba(255,255,255,0.4)", fontSize:"15px", cursor:"pointer", marginBottom:"24px", padding:0 }}>
          ← Back to exercises
        </button>

        {/* TOP: image left, info right */}
        {!mode && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:"24px", marginBottom:"36px", alignItems:"start" }}>

              {/* IMAGE */}
              <div style={{ position:"relative", borderRadius:"18px", overflow:"hidden", border:"1px solid rgba(255,255,255,0.08)", background:"rgba(0,0,0,0.2)", aspectRatio:"4/3" }}>
                <img
                  src={exercise.imageUrl}
                  alt={`${exercise.name} demo`}
                  style={{ width:"100%", height:"100%", objectFit: (exercise.id === "squat" || exercise.id === "pushup") ? "cover" : "contain", objectPosition:"center", background: exercise.id === "lunges" ? "#fff" : "#000" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling?.classList.remove("hidden");
                  }}
                />
                <div className="exercise-image-fallback hidden">
                  <span style={{ fontSize:"5rem" }}>{exercise.emoji}</span>
                  <p style={{ color: theme === "light" ? "#475569" : "rgba(255,255,255,0.5)", margin:0 }}>{exercise.name}</p>
                </div>
              </div>

              {/* INFO */}
              <div style={{ display:"flex", flexDirection:"column", gap:"20px" }}>

                {/* Title + difficulty */}
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"10px", flexWrap:"wrap" }}>
                    <h1 style={{ margin:0, fontSize:"34px", fontWeight:"800", color: theme === "light" ? "#0f172a" : "white" }}>{exercise.name}</h1>
                    {false && (
                      <span style={{ padding:"5px 16px", borderRadius:"20px", fontSize:"13px", fontWeight:"600", background: diff.bg, border:`1px solid ${diff.border}`, color: diff.text }}>
                        {exercise.difficulty}
                      </span>
                    )}
                  </div>
                  <p style={{ margin:0, fontSize:"16px", color: theme === "light" ? "#475569" : "rgba(255,255,255,0.5)", lineHeight:1.7 }}>{exercise.description}</p>
                </div>

                {/* Quick stats */}
                {(exercise.sets || exercise.reps || exercise.calories) && (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"12px" }}>
                    {[
                      { icon:"🔁", label:"Sets",     value: exercise.sets,     color:"#6366f1" },
                      { icon:"⏱️", label:"Duration",  value: exercise.reps,     color:"#06b6d4" },
                      { icon:"🔥", label:"Calories",  value: exercise.calories, color:"#ef4444" },
                    ].map((s, i) => (
                      <div key={i} style={{ padding:"14px 10px", background: theme === "light" ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.05)", borderRadius:"12px", border: `1px solid ${s.color}80`, textAlign:"center" }}>
                        <div style={{ fontSize:"20px", marginBottom:"6px" }}>{s.icon}</div>
                        <div style={{ fontSize:"clamp(12px,3vw,15px)", fontWeight:"700", color: theme === "light" ? "#0f172a" : "white" }}>{s.value}</div>
                        <div style={{ fontSize:"11px", color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.35)", marginTop:"2px" }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Muscles targeted */}
                {exercise.muscles?.length > 0 && (
                  <div>
                    <p style={{ margin:"0 0 10px", fontSize:"13px", fontWeight:"600", color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"1px" }}>Muscles Targeted</p>
                    <div style={{ display:"flex", flexWrap:"nowrap", gap:"8px", width:"100%" }}>
                      {exercise.muscles.map((m, i) => (
                        <span key={i} style={{ flex:"1", textAlign:"center", padding:"6px 0", borderRadius:"20px", fontSize:"13px", fontWeight:"600", background:"rgba(34,197,94,0.15)", border:"1px solid rgba(34,197,94,0.6)", color:"#16a34a", whiteSpace:"nowrap" }}>{m}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* YouTube */}
                <a href={youtubeUrl} target="_blank" rel="noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:"8px", padding:"12px 20px", background:"rgba(220,38,38,0.12)", border:"1px solid rgba(220,38,38,0.6)", borderRadius:"10px", color:"#dc2626", fontSize:"15px", fontWeight:"600", textDecoration:"none", width:"fit-content" }}>
                  ▶ Watch YouTube Demo
                </a>
              </div>
            </div>

            {/* FORM CHECKLIST */}
            {exercise.checklist?.length > 0 && (
              <div style={{ marginBottom:"36px" }}>
                <h2 style={{ margin:"0 0 16px", fontSize:"20px", fontWeight:"700", color: theme === "light" ? "#0f172a" : "white" }}>Form Checklist</h2>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
                  {exercise.checklist.map((tip, i) => (
                    <div key={i} style={{ padding:"16px 18px", background: theme === "light" ? "rgba(255,255,255,0.6)" : "rgba(34,197,94,0.06)", border: theme === "light" ? "1px solid rgba(30,64,175,0.5)" : "1px solid rgba(34,197,94,0.15)", borderRadius:"12px" }}>
                      <span style={{ fontSize:"14px", color: theme === "light" ? "#0f172a" : "rgba(255,255,255,0.75)", lineHeight:1.6 }}>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PRACTICE OPTIONS */}
            <div>
              <h2 style={{ margin:"0 0 16px", fontSize:"20px", fontWeight:"700", color: theme === "light" ? "#0f172a" : "white" }}>How do you want to practice?</h2>
              <div className="exercise-option-cards">
                <button type="button" className="exercise-option-card" onClick={handleLiveStart}>
                  <span className="exercise-option-icon">📹</span>
                  <span className="exercise-option-title">Live Workout</span>
                  <span className="exercise-option-desc">Use your camera for real-time analysis</span>
                </button>
                <button type="button" className="exercise-option-card" onClick={() => setMode("upload")}>
                  <span className="exercise-option-icon">📤</span>
                  <span className="exercise-option-title">Upload a Video</span>
                  <span className="exercise-option-desc">Upload a video of this exercise only</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* LIVE MODE */}
        {mode === "live" && (
          <div className="exercise-live-wrap">
            <h2 style={{ margin:"0 0 16px", fontSize:"22px", fontWeight:"700", color: theme === "light" ? "#0f172a" : "white" }}>{exercise.name} — Live</h2>
            {analyzing && <p className="exercise-analyzing">⏳ Starting camera & loading pose model…</p>}
            {liveError && <p style={{ color:"#fca5a5" }}>{liveError}</p>}
            <div style={{ display:"flex", flexDirection:"row", flexWrap:"wrap", gap:"16px", alignItems:"stretch" }}>
              <div style={{ flex:"1 1 60%", minWidth:"280px", display:"flex", flexDirection:"column", gap:"12px" }}>
                <div className={`exercise-video-container${liveFeedback ? (liveFeedback.posture_ok ? " posture-correct" : " posture-incorrect") : ""}`} style={{ height:"60vh", width:"100%", maxWidth:"none", borderRadius:"12px", overflow:"hidden", position:"relative" }}>
                  <video ref={videoRef} className="exercise-video" autoPlay playsInline muted webkit-playsinline="true" style={{width:"100%", height:"100%", objectFit:"cover", display:"block"}} />
                  <canvas ref={canvasRef} className="exercise-pose-canvas" />
                </div>
                {liveFeedback && (
                  <div className={`exercise-feedback${liveFeedback.posture_ok ? " posture-correct" : " posture-incorrect"}`} style={{ width:"100%", maxWidth:"none" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"4px", flexWrap: window.innerWidth < 640 ? "wrap" : "nowrap" }}>
                      <p className="exercise-feedback-message" style={{margin:0}}>{liveFeedback.message || (liveFeedback.posture_ok ? "✅ Good form!" : "⚠️ Adjust your form")}</p>
                      <div style={{ display:"flex", gap:"8px", width: window.innerWidth < 640 ? "100%" : "auto", marginTop: window.innerWidth < 640 ? "8px" : "0" }}>
                        <button type="button" onClick={() => { isMutedRef.current = !isMutedRef.current; setIsMuted(isMutedRef.current); window.speechSynthesis.cancel(); }} style={{ display:"flex", alignItems:"center", gap:"6px", background:"linear-gradient(135deg,#7c3aed,#06b6d4)", border:"none", borderRadius:"8px", color:"white", padding:"6px 14px", fontSize:"13px", fontWeight:"600", cursor:"pointer" }}>
                          {isMuted ? "🔇 Unmute" : "🔊 Mute"}
                        </button>
                        <button type="button" onClick={() => isRecording ? stopRecording() : startRecording()} style={{ display:"flex", alignItems:"center", gap:"6px", background: isRecording ? "linear-gradient(135deg,#ef4444,#dc2626)" : "linear-gradient(135deg,#7c3aed,#06b6d4)", border:"none", borderRadius:"8px", color:"white", padding:"6px 14px", fontSize:"13px", fontWeight:"600", cursor:"pointer" }}>
                          {isRecording ? <><span style={{ width:"8px", height:"8px", borderRadius:"50%", background:"white", display:"inline-block" }}></span> Stop & Save</> : <>🔴 Record</>}
                        </button>
                      </div>
                    </div>
                    {liveFeedback != null && <p className="exercise-feedback-accuracy">Accuracy: {liveFeedback.accuracy != null && (exerciseId === "tree-pose" || exerciseId === "plank" ? plankSeconds > 0 : repAccuraciesRef.current.length >= 3) ? Math.round(liveFeedback.accuracy) : 0}%</p>}
                    {(exerciseId === "plank" || exerciseId === "tree-pose") && (
                      <p className="exercise-feedback-counter" style={{fontSize:"20px", fontWeight:"700", color: liveFeedback.posture_ok ? "#4ade80" : "#fca5a5"}}>
                        ⏱ {Math.floor(plankSeconds / 60).toString().padStart(2,"0")}:{(plankSeconds % 60).toString().padStart(2,"0")}
                        <button type="button" className="btn ghost" onClick={() => { plankSecondsRef.current = 0; setPlankSeconds(0); }} style={{ marginLeft:"12px", fontSize:"12px", padding:"3px 10px", minWidth:"auto" }}>Reset</button>
                      </p>
                    )}
                    {liveFeedback.counter != null && (
                      <p className="exercise-feedback-counter">
                        Reps: {liveFeedback.counter - startCounterRef.current > 0 ? liveFeedback.counter - startCounterRef.current : 0}
                        <button type="button" className="btn ghost" onClick={handleResetCounter} style={{ marginLeft:"12px", fontSize:"12px", padding:"3px 10px", minWidth:"auto" }}>Reset</button>
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div style={{ flex:"1 1 240px", minWidth:"240px", display:"flex", flexDirection:"column", gap:"12px" }}>
                <p style={{ fontWeight:"600", margin:"0 0 4px", fontSize:"15px", color: theme === "light" ? "#0f172a" : "#e2e8f0" }}>💡 Tips for Best Results</p>
                {[
                  { icon:"💪", title:"Warm up first", desc:"Spend 5 minutes warming up before any session to prevent injuries.", color:"#6366f1" },
                  { icon:"📸", title:"Good lighting", desc:"Make sure your full body is visible and well-lit for best AI tracking.", color:"#06b6d4" },
                  { icon:"👟", title:"Wear fitted clothes", desc:"Fitted clothing helps the AI detect your joints more accurately.", color:"#eab308" },
                  { icon:"📏", title:"Stand back", desc:"Keep 5–8 feet of distance from the camera for full body detection.", color:"#ef4444" },
                ].map((tip, i) => (
                  <div key={i} style={{ background: theme === "light" ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.04)", border: `1px solid ${tip.color}80`, borderRadius:"12px", padding:"16px", flex:1 }}>
                    <span style={{ fontSize:"22px", display:"block", marginBottom:"8px" }}>{tip.icon}</span>
                    <p style={{ fontWeight:"600", margin:"0 0 4px", fontSize:"13px", color: theme === "light" ? "#0f172a" : "#e2e8f0" }}>{tip.title}</p>
                    <p style={{ margin:0, fontSize:"12px", color: theme === "light" ? "#475569" : "#94a3b8", lineHeight:"1.5" }}>{tip.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* UPLOAD MODE */}
        {mode === "upload" && (
          <div className="exercise-upload-wrap">

            <h2 style={{ margin:"0 0 16px", fontSize:"22px", fontWeight:"700", color: theme === "light" ? "#0f172a" : "white" }}>{exercise.name} — Upload Video</h2>
            <label className="exercise-upload-label" style={{ display:"flex", flexDirection: window.innerWidth < 640 ? "column" : "row", alignItems: window.innerWidth < 640 ? "flex-start" : "center", gap:"12px" }}>
              <span style={{ fontSize:"14px", fontWeight:"600", color: theme === "light" ? "#0f172a" : "white" }}>Select video file:</span>
              <span style={{ padding:"8px 20px", borderRadius:"10px", background:"linear-gradient(135deg,#7c3aed,#06b6d4)", color:"white", fontSize:"13px", fontWeight:"600", cursor:"pointer" }}>Choose File</span>
              <input type="file" accept="video/*" onChange={handleUploadChange} className="exercise-upload-input" style={{ display:"none" }} />
            {!analyzing && !uploadSuccess && uploadVideoUrl && (
              <button type="button" className="btn primary" onClick={handleUploadSubmit} style={{ marginLeft: window.innerWidth < 640 ? "0" : "12px" }}>Analyse Video</button>
            )}
            {analyzing && <span style={{marginLeft:"16px", fontSize:"14px", fontWeight:"600", color:"#06b6d4"}}>Analysing... {uploadProgress}%</span>}
            </label>
            <>
              <div style={{ display:"flex", flexDirection:"column", gap:"16px", marginTop:"8px", width:"100%" }}>
                {uploadSuccess && uploadResult && (
                  <div style={{ background: theme === "light" ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.05)", border: theme === "light" ? "1px solid rgba(30,64,175,0.2)" : "1px solid rgba(255,255,255,0.1)", borderRadius:"12px", padding:"20px", fontSize:"14px", boxShadow: theme === "light" ? "0 2px 12px rgba(0,0,0,0.06)" : "none" }}>
                    <p className="exercise-upload-success" style={{ fontWeight:"700", fontSize:"16px", color: theme === "light" ? "#0f172a" : "white", marginBottom:"8px" }}>Analysis Complete</p>
                    {uploadResult.total_reps != null && (
                      exerciseId === "plank" || exerciseId === "tree-pose"
                        ? <p className="muted small">Hold time: {uploadResult.total_reps} seconds</p>
                        : <p className="muted small">Reps detected: {uploadResult.total_reps}</p>
                    )}
                    {uploadResult.avg_accuracy != null && <p className="muted small">Avg accuracy: {Math.round(uploadResult.avg_accuracy)}%</p>}
                    {uploadResult.feedback && <p style={{ margin:"6px 0 0", fontWeight:"600", color: uploadResult.avg_accuracy >= 90 ? "#4ade80" : uploadResult.avg_accuracy >= 75 ? "#06b6d4" : uploadResult.avg_accuracy >= 60 ? "#fbbf24" : "#fca5a5", fontSize:"13px" }}>{uploadResult.feedback}</p>}
                    {uploadResult.suggestions && uploadResult.suggestions.length > 0 && (
                      <div style={{ marginTop:"12px", borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:"12px" }}>
                        <p style={{ margin:"0 0 8px", fontSize:"13px", fontWeight:"600", color: theme === "light" ? "#0f172a" : "#e2e8f0" }}>Tips to improve your score:</p>
                        {uploadResult.suggestions.map((tip, i) => (
                          <div key={i} style={{ display:"flex", gap:"8px", alignItems:"flex-start", marginBottom:"6px" }}>
                            <span style={{ color:"#06b6d4", fontWeight:"700", flexShrink:0 }}>→</span>
                            <p style={{ margin:0, fontSize:"12px", color: theme === "light" ? "#475569" : "#94a3b8", lineHeight:"1.5" }}>{tip}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display:"flex", flexDirection:"row", flexWrap:"wrap", gap:"16px", alignItems:"flex-end", width:"100%" }}>
                  <div style={{ flex:"1 1 60%", minWidth:"280px", borderRadius:"12px", overflow:"hidden", border:"1px solid rgba(255,255,255,0.1)", height:"60vh", background:"rgba(0,0,0,0.3)", display:"flex", flexDirection:"column" }}>
                    {uploadVideoUrl ? (
                      <>
                      <video
                        id="upload-preview-video"
                        src={uploadVideoUrl}
                        muted
                        style={{ width:"100%", height:"100%", display:"block", background:"#000", objectFit:"cover", flex:1 }}
                      />
                      {analyzing && (
                        <div style={{ padding:"10px 16px", background:"rgba(6,182,212,0.08)", display:"flex", alignItems:"center", gap:"12px" }}>
                          <div style={{ flex:1, height:"6px", background:"rgba(255,255,255,0.1)", borderRadius:"4px", overflow:"hidden" }}>
                            <div style={{ height:"100%", width:`${uploadProgress}%`, background:"linear-gradient(90deg,#7c3aed,#06b6d4)", borderRadius:"4px", transition:"width 0.3s ease" }} />
                          </div>
                          <span style={{ fontSize:"13px", color:"#67e8f9", whiteSpace:"nowrap" }}>{uploadProgress}%</span>
                        </div>
                      )}
                      </>
                    ) : (
                      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color: theme === "light" ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.15)" }}>
                        <div style={{ fontSize:"64px", marginBottom:"16px" }}>🎥</div>
                        <p style={{ margin:0, fontSize:"15px" }}>Your video will appear here</p>
                        <p style={{ margin:"6px 0 0", fontSize:"12px", color: theme === "light" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.1)" }}>Select a file above to get started</p>
                      </div>
                    )}
                  </div>
                  <div style={{ flex:"1 1 240px", minWidth:"240px", display:"flex", flexDirection:"column", gap:"12px" }}>
                    <p style={{ fontWeight:"600", margin:"0 0 4px", fontSize:"15px", color: theme === "light" ? "#0f172a" : "#e2e8f0" }}>💡 Tips for Best Results</p>
                    {[
                      { icon:"💪", title:"Warm up first", desc:"Spend 5 minutes warming up before any session to prevent injuries.", color:"#6366f1" },
                      { icon:"📸", title:"Good lighting", desc:"Make sure your full body is visible and well-lit for best AI tracking.", color:"#06b6d4" },
                      { icon:"👟", title:"Wear fitted clothes", desc:"Fitted clothing helps the AI detect your joints more accurately.", color:"#eab308" },
                      { icon:"📏", title:"Stand back", desc:"Keep 5–8 feet of distance from the camera for full body detection.", color:"#ef4444" },
                    ].map((tip, i) => (
                      <div key={i} style={{ background: theme === "light" ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.04)", border: `1px solid ${tip.color}80`, borderRadius:"12px", padding:"16px", flex:1 }}>
                        <span style={{ fontSize:"22px", display:"block", marginBottom:"8px" }}>{tip.icon}</span>
                        <p style={{ fontWeight:"600", margin:"0 0 4px", fontSize:"13px", color: theme === "light" ? "#0f172a" : "#e2e8f0" }}>{tip.title}</p>
                        <p style={{ margin:0, fontSize:"12px", color: theme === "light" ? "#475569" : "#94a3b8", lineHeight:"1.5" }}>{tip.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
            {uploadError && <p style={{ color:"#fca5a5", margin:0 }}>⚠️ {uploadError}</p>}
          </div>
        )}

      </main>
    </div>
  );
}
// session tracking enabled
